import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  X,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import MediaPicker from "@/admin/components/MediaPicker";
import RichTextEditor from "@/admin/components/RichTextEditor";
import SitePageDraftPanel from "@/admin/SitePageDraftPanel";
import { BLOCK_SCHEMAS, BLOCK_TYPES, labelFor, schemaFor } from "@/components/blocks/registry";

// Slugs whose public routes render through <BlockRendererPage>. Kept in sync
// with the same set in AdminSitePagesPage.jsx. When you migrate a new page
// to the CMS, add its slug here too so the phantom-edit warning disappears.
const CMS_RENDERED_SLUGS = new Set([
  "privacy", "terms", "security", "accessibility",
  "advance", "aether",
  "solutions-distributors", "solutions-suppliers", "solutions-decorators",
  "solutions-print-shops", "solutions-sign-companies", "solutions-quickbooks",
  "home",
  "user-groups",
  // /developers → composite api_docs_page block
  "developers",
  // /solutions → composite solutions_landing_page block
  "solutions",
  // /products → composite products_landing_page block
  "products",
  // /exchange → composite exchange_page block
  "exchange",
  // /exchange/elite-supplier → composite elite_supplier_page block
  "elite-supplier",
  // /academy → composite academy_page block
  "academy",
  // /integrations → composite integrations_page block
  "integrations",
  // /events, /openhouse, /customer-portal → composite blocks
  "events",
  "openhouse",
  "customer-portal",
  "careers-intro",
]);

/**
 * Amber banner shown at the top of the editor for pages that are registered
 * in the admin registry but whose public route still renders from a hardcoded
 * .jsx file. Saving still works — but edits will NOT appear on the live site
 * until the page is migrated to render through <BlockRendererPage>.
 *
 * Contact is a special case: its content IS editable, just at a different URL
 * (/admin/site-settings → Contact section). We show a blue "look here instead"
 * banner rather than the generic amber "not editable" one.
 */
function PhantomEditWarning({ slug }) {
  const baseSlug = slug?.replace(/__draft$|__backup$/, "");
  if (!baseSlug || CMS_RENDERED_SLUGS.has(baseSlug)) return null;

  if (baseSlug === "contact") {
    return (
      <div
        data-testid="admin-editor-phantom-warning"
        className="border border-sky-300 bg-sky-50 rounded-xl p-4 flex gap-3 items-start"
      >
        <div className="pt-0.5">
          <svg viewBox="0 0 24 24" width="18" height="18" className="text-sky-700" fill="currentColor" aria-hidden>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm1 15h-2v-6h2v6Zm0-8h-2V7h2v2Z" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-sky-900">
            Contact page content lives in Site Settings, not here.
          </p>
          <p className="text-xs text-sky-800 mt-1 leading-relaxed">
            The Contact page reads its hero, address, phone, hours, form
            heading, and CTA labels from <strong>Site Settings → Contact</strong>.
            Edits made here on <code className="font-mono text-[11px]">/admin/site-pages/contact</code> won&apos;t reflect on the
            live site. Open <a href="/admin/site-settings" className="text-sky-700 underline font-semibold">Site Settings</a>
            &nbsp;instead to change the copy visitors see.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-editor-phantom-warning"
      className="border border-amber-300 bg-amber-50 rounded-xl p-4 flex gap-3 items-start"
    >
      <div className="pt-0.5">
        <svg viewBox="0 0 24 24" width="18" height="18" className="text-amber-700" fill="currentColor" aria-hidden>
          <path d="M12 2 1 21h22L12 2Zm0 6 8 14H4l8-14Zm-1 5v4h2v-4h-2Zm0 5v2h2v-2h-2Z"/>
        </svg>
      </div>
      <div className="flex-1">
        <p className="text-sm font-bold text-amber-900">
          Edits here will not appear on the live site yet.
        </p>
        <p className="text-xs text-amber-800 mt-1 leading-relaxed">
          The public page at <code className="font-mono text-[11px]">/{baseSlug}</code> is currently rendered from
          hardcoded page code, not from these blocks. You can save changes and preview them via the draft panel below,
          but they won&apos;t reflect on the live site until this page is migrated to the block-based CMS. Ask your
          engineering team to migrate <code className="font-mono text-[11px]">{baseSlug}</code> &mdash; the pattern
          (<em>solution_detail_page</em> or per-page composite block) is already established.
        </p>
      </div>
    </div>
  );
}


function JsonField({ value, onChange, label, placeholder, testId }) {
  const [text, setText] = useState(() =>
    value === undefined || value === null ? "" : JSON.stringify(value, null, 2)
  );  const [error, setError] = useState("");

  // Sync when the outer value changes (e.g. after Save reloads the block).
  useEffect(() => {
    const nextText = value === undefined || value === null ? "" : JSON.stringify(value, null, 2);
    setText(nextText);
    setError("");
  }, [value]);

  const commit = () => {
    if (text.trim() === "") {
      setError("");
      onChange(undefined);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      setError("");
      onChange(parsed);
    } catch (exc) {
      setError(String(exc.message || exc));
    }
  };

  return (
    <div>
      {label}
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        placeholder={placeholder}
        className="mt-1 min-h-[180px] text-xs font-mono"
        data-testid={testId}
      />
      {error ? (
        <p className="mt-1 text-xs text-red-600" data-testid={`${testId}-error`}>
          Invalid JSON: {error}
        </p>
      ) : (
        <p className="mt-1 text-[11px] text-slate-400">
          Edits are saved when you click outside this box. Format must be valid JSON.
        </p>
      )}
    </div>
  );
}


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generates a stable-enough client-side id for new blocks. The backend will
// replace missing ids on save; this is only used to key React lists.
function newId() {
  return "b_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// Renders one form field based on the registry schema entry.
// onOpenPicker(fieldKey, itemIdx?) is called by image fields to open the
// site-wide media picker. `itemIdx` is set when the field lives inside an
// object_list item so the parent knows which list item to patch.
// Renders a single sub-field inside an object_list item. Supports textarea,
// select (string or {value,label} options), list (bullet list of strings),
// nested object_list (used by solution_detail_page.sections[].points[]),
// image (picker), and plain text (fallback). Extracted from BlockField so the
// deeply-nested JSX doesn't blow up the Babel traversal stack.
function ItemFieldEditor({ f, val, set, onOpenPicker, parentIdx }) {
  if (f.type === "textarea") {
    return (
      <div key={f.key}>
        <Label className="text-[11px] font-semibold text-slate-600">{f.label}</Label>
        <Textarea value={val || ""} onChange={(e) => set(e.target.value)}
          className="mt-1 text-sm min-h-[70px]" />
      </div>
    );
  }
  if (f.type === "select") {
    const options = (f.options || []).map((o) =>
      typeof o === "string" ? { value: o, label: o } : o
    );
    return (
      <div key={f.key}>
        <Label className="text-[11px] font-semibold text-slate-600">{f.label}</Label>
        <select value={val || ""} onChange={(e) => set(e.target.value)}
          className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white text-sm px-2 focus:outline-none focus:ring-1 focus:ring-sky-500">
          <option value="">— Select —</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }
  if (f.type === "list") {
    const listVal = Array.isArray(val) ? val : [];
    return (
      <div key={f.key}>
        <Label className="text-[11px] font-semibold text-slate-600">{f.label}</Label>
        <div className="mt-1 space-y-1.5">
          {listVal.map((entry, li) => (
            <ListRow key={li} entry={entry} onEdit={(next) => {
              const arr = [...listVal]; arr[li] = next; set(arr);
            }} onRemove={() => set(listVal.filter((_, j) => j !== li))} />
          ))}
          <Button type="button" variant="outline" size="sm"
            onClick={() => set([...listVal, ""])} className="h-6 text-[10px]">
            <Plus className="h-2.5 w-2.5 mr-1" /> Add
          </Button>
        </div>
      </div>
    );
  }
  if (f.type === "object_list") {
    return <NestedObjectList field={f} value={val} onChange={set} />;
  }
  if (f.type === "image") {
    return (
      <div key={f.key}>
        <Label className="text-[11px] font-semibold text-slate-600">{f.label}</Label>
        <div className="mt-1 flex items-start gap-3">
          {val ? (
            <img src={val.startsWith("http") ? val : `${process.env.REACT_APP_BACKEND_URL}${val}`}
              alt="" className="h-14 w-20 rounded object-contain border border-slate-200 bg-white" />
          ) : (
            <div className="h-14 w-20 rounded bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
              <ImageIcon className="h-4 w-4" />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <Button type="button" variant="outline" size="sm"
              onClick={() => onOpenPicker(f.key, parentIdx)} className="h-7 text-[11px]">
              <ImageIcon className="h-3 w-3 mr-1" />{val ? "Replace" : "Choose"}
            </Button>
            {val && (
              <Button type="button" variant="ghost" size="sm" onClick={() => set("")}
                className="h-6 text-[10px] text-red-500 hover:text-red-600 hover:bg-red-50 justify-start px-2">
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  // fallback: single-line text
  return (
    <div key={f.key}>
      <Label className="text-[11px] font-semibold text-slate-600">{f.label}</Label>
      <Input value={val || ""} onChange={(e) => set(e.target.value)}
        className="mt-1 h-8 text-sm" />
    </div>
  );
}

function ListRow({ entry, onEdit, onRemove }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-slate-400 w-4 text-right">•</span>
      <Input value={entry || ""} onChange={(e) => onEdit(e.target.value)}
        className="flex-1 h-7 text-sm" />
      <button type="button" onClick={onRemove}
        className="p-1 rounded text-slate-400 hover:bg-red-50 hover:text-red-500" title="Remove">
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

// Nested object_list rendered inside a parent object_list item. Supports one
// level of nesting (parent.sections[].points[] is enough for the current CMS).
function NestedObjectList({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const fields = field.item_fields || [];
  const blank = () => fields.reduce((acc, nf) => ({ ...acc, [nf.key]: "" }), {});
  const update = (idx, key, next) => {
    const arr = [...items]; arr[idx] = { ...arr[idx], [key]: next }; onChange(arr);
  };
  const move = (idx, dir) => {
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= items.length) return;
    const arr = [...items]; [arr[idx], arr[j]] = [arr[j], arr[idx]]; onChange(arr);
  };
  return (
    <div>
      <Label className="text-[11px] font-semibold text-slate-600">{field.label}</Label>
      <div className="mt-1 space-y-2 pl-2 border-l-2 border-slate-200">
        {items.map((it, idx) => (
          <NestedRow key={idx} item={it} idx={idx} isFirst={idx === 0} isLast={idx === items.length - 1}
            fields={fields}
            onUpdate={(key, next) => update(idx, key, next)}
            onMove={(dir) => move(idx, dir)}
            onRemove={() => onChange(items.filter((_, j) => j !== idx))} />
        ))}
        <Button type="button" variant="outline" size="sm"
          onClick={() => onChange([...items, blank()])} className="h-6 text-[10px]">
          <Plus className="h-2.5 w-2.5 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function NestedRow({ item, idx, isFirst, isLast, fields, onUpdate, onMove, onRemove }) {
  return (
    <div className="border border-slate-200 rounded p-2 bg-white space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[9px] uppercase font-bold tracking-widest text-slate-400">
          Item {idx + 1}
        </span>
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => onMove("up")} disabled={isFirst}
            className="p-1 rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30" title="Move up">
            <ArrowUp className="h-3 w-3" />
          </button>
          <button type="button" onClick={() => onMove("down")} disabled={isLast}
            className="p-1 rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30" title="Move down">
            <ArrowDown className="h-3 w-3" />
          </button>
          <button type="button" onClick={onRemove}
            className="p-1 rounded text-slate-400 hover:bg-red-50 hover:text-red-500" title="Remove">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      {fields.map((nf) => {
        const nval = item[nf.key] || "";
        if (nf.type === "textarea") {
          return (
            <div key={nf.key}>
              <Label className="text-[10px] font-semibold text-slate-600">{nf.label}</Label>
              <Textarea value={nval} onChange={(e) => onUpdate(nf.key, e.target.value)}
                className="mt-1 text-sm min-h-[60px]" />
            </div>
          );
        }
        return (
          <div key={nf.key}>
            <Label className="text-[10px] font-semibold text-slate-600">{nf.label}</Label>
            <Input value={nval} onChange={(e) => onUpdate(nf.key, e.target.value)}
              className="mt-1 h-7 text-sm" />
          </div>
        );
      })}
    </div>
  );
}

// Group of subfields — renders a labelled panel with each subfield rendered
// as a leaf control (text / textarea / select / image / object_list). Kept
// non-recursive on purpose: recursive JSX references trip the emergentbase
// visual-edits Babel plugin's static-analysis pass.
function GroupField({ field, value, onChange, onOpenPicker }) {
  const obj = (value && typeof value === "object" && !Array.isArray(value)) ? value : {};
  const subfields = field.subfields || [];
  const setSub = (subkey, next) => onChange({ ...obj, [subkey]: next });
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40">
      <div className="text-[11px] uppercase font-bold tracking-widest text-slate-500 mb-3">
        {field.label}
      </div>
      {field.help ? <p className="text-[11px] text-slate-500 -mt-2 mb-3">{field.help}</p> : null}
      <div className="space-y-3">
        {subfields.map((sf) => (
          <GroupSubfieldRenderer
            key={sf.key}
            sf={sf}
            val={obj[sf.key]}
            set={(next) => setSub(sf.key, next)}
            onOpenPicker={onOpenPicker}
            groupKey={field.key}
          />
        ))}
      </div>
    </div>
  );
}

// Renders a single subfield inside a GroupField. Only leaf types allowed
// (text, textarea, select, image, list, object_list). No recursion into
// BlockField or another GroupField — CMS schemas should keep group depth = 1.
function GroupSubfieldRenderer({ sf, val, set, onOpenPicker, groupKey }) {
  const label = <Label className="text-xs font-semibold text-slate-700">{sf.label}</Label>;
  if (sf.type === "textarea") {
    return (
      <div>
        {label}
        <Textarea value={val || ""} onChange={(e) => set(e.target.value)}
          className="mt-1 min-h-[80px] text-sm" />
      </div>
    );
  }
  if (sf.type === "select") {
    const options = (sf.options || []).map((o) =>
      typeof o === "string" ? { value: o, label: o } : o
    );
    return (
      <div>
        {label}
        <select value={val || ""} onChange={(e) => set(e.target.value)}
          className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white text-sm px-3 focus:outline-none focus:ring-1 focus:ring-sky-500">
          {sf.placeholder ? <option value="">{sf.placeholder}</option> : null}
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }
  if (sf.type === "image") {
    return (
      <div>
        {label}
        <div className="mt-1 flex items-start gap-4">
          {val ? (
            <img src={val.startsWith("http") ? val : `${process.env.REACT_APP_BACKEND_URL}${val}`}
              alt="" className="h-20 w-32 rounded-lg object-cover border border-slate-200" />
          ) : (
            <div className="h-20 w-32 rounded-lg bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
              <ImageIcon className="h-5 w-5" />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Button type="button" variant="outline" size="sm"
              onClick={() => onOpenPicker(`${groupKey}.${sf.key}`)} className="h-8 text-xs">
              <ImageIcon className="h-3.5 w-3.5 mr-1" />{val ? "Replace" : "Choose"}
            </Button>
            {val && (
              <Button type="button" variant="ghost" size="sm" onClick={() => set("")}
                className="h-7 text-[11px] text-red-500 hover:text-red-600 hover:bg-red-50 justify-start">
                <X className="h-3.5 w-3.5 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (sf.type === "list") {
    const listVal = Array.isArray(val) ? val : [];
    return (
      <div>
        {label}
        <div className="mt-1 space-y-1.5">
          {listVal.map((entry, i) => (
            <ListRow key={i} entry={entry}
              onEdit={(next) => { const a = [...listVal]; a[i] = next; set(a); }}
              onRemove={() => set(listVal.filter((_, j) => j !== i))} />
          ))}
          <Button type="button" variant="outline" size="sm"
            onClick={() => set([...listVal, ""])} className="h-7 text-[11px]">
            <Plus className="h-3 w-3 mr-1" /> Add item
          </Button>
        </div>
      </div>
    );
  }
  if (sf.type === "object_list") {
    // Uses NestedObjectList — same component that powers nested rows inside
    // an object_list item. Keeps the group depth at 1 (no group-in-group).
    return <NestedObjectList field={sf} value={val} onChange={set} />;
  }
  // default: text
  return (
    <div>
      {label}
      <Input value={val || ""} onChange={(e) => set(e.target.value)}
        className="mt-1 h-9 text-sm" />
    </div>
  );
}

function BlockField({ field, value, onChange, onOpenPicker }) {
  const commonLabel = (
    <Label className="text-xs font-semibold text-slate-700">{field.label}</Label>
  );

  if (field.type === "group") {
    return (
      <GroupField
        field={field}
        value={value}
        onChange={onChange}
        onOpenPicker={onOpenPicker}
      />
    );
  }

  if (field.type === "richtext") {
    return (
      <div>
        {commonLabel}
        <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden">
          <RichTextEditor
            value={value || ""}
            onChange={(html) => onChange(html)}
            placeholder="Start writing…"
            testId={`richtext-${field.key}`}
          />
        </div>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div>
        {commonLabel}
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 min-h-[90px] text-sm"
        />
      </div>
    );
  }

  if (field.type === "json") {
    // Nested-object editor for complex block data (e.g. solution page
    // sections with points). Renders as a monospace textarea and parses on
    // blur; the field-level error banner tells the admin if the JSON is
    // invalid without dropping their in-progress edit.
    return (
      <JsonField
        value={value}
        onChange={onChange}
        label={commonLabel}
        placeholder={field.placeholder || "{}"}
        testId={`json-${field.key}`}
      />
    );
  }

  if (field.type === "select") {
    const options = (field.options || []).map((o) =>
      typeof o === "string" ? { value: o, label: o } : o
    );
    return (
      <div>
        {commonLabel}
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white text-sm px-3 focus:outline-none focus:ring-1 focus:ring-sky-500"
        >
          {field.placeholder ? <option value="">{field.placeholder}</option> : null}
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "image") {
    return (
      <div>
        {commonLabel}
        <div className="mt-1 flex items-start gap-4">
          {value ? (
            <img
              src={value.startsWith("http") ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`}
              alt=""
              className="h-20 w-32 rounded-lg object-cover border border-slate-200"
            />
          ) : (
            <div className="h-20 w-32 rounded-lg bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
              <ImageIcon className="h-5 w-5" />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenPicker(field.key)}
              className="h-8 text-xs"
            >
              <ImageIcon className="h-3.5 w-3.5 mr-1" />
              {value ? "Replace" : "Choose"}
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange("")}
                className="h-7 text-[11px] text-red-500 hover:text-red-600 hover:bg-red-50 justify-start"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (field.type === "list") {
    const items = Array.isArray(value) ? value : [];
    return (
      <div>
        {commonLabel}
        <div className="mt-1 space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-slate-400 w-4 text-right">•</span>
              <Input
                value={item}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = e.target.value;
                  onChange(next);
                }}
                className="flex-1 h-8 text-sm"
              />
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                title="Remove"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange([...items, ""])}
            className="h-7 text-[11px]"
          >
            <Plus className="h-3 w-3 mr-1" /> Add item
          </Button>
        </div>
      </div>
    );
  }

  if (field.type === "object_list") {
    const items = Array.isArray(value) ? value : [];
    const itemFields = field.item_fields || [];
    const blankItem = () =>
      itemFields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});
    const updateItem = (idx, key, next) => {
      const arr = [...items];
      arr[idx] = { ...arr[idx], [key]: next };
      onChange(arr);
    };
    const moveItem = (idx, direction) => {
      const next = direction === "up" ? idx - 1 : idx + 1;
      if (next < 0 || next >= items.length) return;
      const arr = [...items];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      onChange(arr);
    };
    return (
      <div>
        {commonLabel}
        <div className="mt-1 space-y-2">
          {items.map((it, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-lg p-3 bg-slate-50/40 space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400">
                  Item {idx + 1}
                </span>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => moveItem(idx, "up")}
                    disabled={idx === 0}
                    className="p-1 rounded text-slate-400 hover:bg-slate-200 disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(idx, "down")}
                    disabled={idx === items.length - 1}
                    className="p-1 rounded text-slate-400 hover:bg-slate-200 disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange(items.filter((_, j) => j !== idx))}
                    className="p-1 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {itemFields.map((f) => (
                <ItemFieldEditor
                  key={f.key}
                  f={f}
                  val={it[f.key]}
                  set={(next) => updateItem(idx, f.key, next)}
                  onOpenPicker={onOpenPicker}
                  parentIdx={idx}
                />
              ))}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange([...items, blankItem()])}
            className="h-7 text-[11px]"
          >
            <Plus className="h-3 w-3 mr-1" /> Add item
          </Button>
        </div>
      </div>
    );
  }

  // default: text
  return (
    <div>
      {commonLabel}
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 text-sm"
      />
    </div>
  );
}


// One collapsible card representing a single block on the page.
function BlockCard({
  block,
  index,
  totalCount,
  isOpen,
  onToggle,
  onMove,
  onRemove,
  onChange,
  onOpenPicker,
}) {
  const schema = schemaFor(block.type);
  if (!schema) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-lg p-3 text-xs text-red-700">
        Unknown block type: {block.type}
      </div>
    );
  }

  const setDataField = (key) => (val) =>
    onChange({ ...block, data: { ...block.data, [key]: val } });

  return (
    <div
      className="border border-slate-200 rounded-lg bg-white"
      data-testid={`admin-block-${block.id}`}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/50">
        <button
          type="button"
          onClick={onToggle}
          className="p-1 rounded text-slate-500 hover:bg-slate-200"
          title={isOpen ? "Collapse" : "Expand"}
          data-testid={`admin-block-toggle-${block.id}`}
        >
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <span className="text-xs font-mono text-slate-400 w-6 text-right">#{index + 1}</span>
        <span className="text-xs font-bold uppercase tracking-widest text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2 py-0.5">
          {labelFor(block.type)}
        </span>
        <span className="text-xs text-slate-500 truncate flex-1 min-w-0">
          {block.data?.title || block.data?.heading || schema.description}
        </span>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => onMove("up")}
            disabled={index === 0}
            data-testid={`admin-block-up-${block.id}`}
            className="p-1 rounded text-slate-400 hover:bg-slate-200 disabled:opacity-30"
            title="Move up"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMove("down")}
            disabled={index === totalCount - 1}
            data-testid={`admin-block-down-${block.id}`}
            className="p-1 rounded text-slate-400 hover:bg-slate-200 disabled:opacity-30"
            title="Move down"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            data-testid={`admin-block-remove-${block.id}`}
            className="p-1 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
            title="Delete block"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {isOpen && (
        <div className="p-4 space-y-3">
          {schema.fields.map((f) => (
            <BlockField
              key={f.key}
              field={f}
              value={block.data?.[f.key]}
              onChange={setDataField(f.key)}
              onOpenPicker={(fieldKey, itemIdx) =>
                onOpenPicker(block.id, fieldKey ?? f.key, itemIdx)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Block editor for a single site page. Loads /admin/site-pages/{slug}, lets the
 * admin add / edit / reorder / remove blocks, then PUTs the full doc back.
 */
export default function AdminSitePageEditor() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [openIds, setOpenIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState({ open: false, blockId: null, fieldKey: null, itemIdx: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/site-pages/${slug}`);
        // Guarantee every block has an id so React keys stay stable.
        const blocks = (data.blocks || []).map((b) => ({
          ...b,
          id: b.id || newId(),
        }));
        setPage({ ...data, blocks });
      } catch {
        toast.error("Could not load page.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading…</div>;
  if (!page) return null;

  const setBlocks = (updater) =>
    setPage((prev) => ({ ...prev, blocks: updater(prev.blocks) }));

  const patchBlock = (id, next) =>
    setBlocks((blocks) => blocks.map((b) => (b.id === id ? next : b)));

  const removeBlock = (id) => {
    if (!window.confirm("Remove this block?")) return;
    setBlocks((blocks) => blocks.filter((b) => b.id !== id));
  };

  const moveBlock = (id, direction) => {
    setBlocks((blocks) => {
      const idx = blocks.findIndex((b) => b.id === id);
      const next = direction === "up" ? idx - 1 : idx + 1;
      if (next < 0 || next >= blocks.length) return blocks;
      const copy = [...blocks];
      [copy[idx], copy[next]] = [copy[next], copy[idx]];
      return copy;
    });
  };

  const addBlock = (type) => {
    const schema = schemaFor(type);
    if (!schema) return;
    const block = { id: newId(), type, data: { ...schema.defaults } };
    setBlocks((blocks) => [...blocks, block]);
    setOpenIds((prev) => ({ ...prev, [block.id]: true }));
  };

  const openPicker = (blockId, fieldKey, itemIdx = null) =>
    setPicker({ open: true, blockId, fieldKey, itemIdx });

  const closePicker = () =>
    setPicker({ open: false, blockId: null, fieldKey: null, itemIdx: null });

  const onPickImage = (url) => {
    const { blockId, fieldKey, itemIdx } = picker;
    setBlocks((blocks) =>
      blocks.map((b) => {
        if (b.id !== blockId) return b;
        if (itemIdx == null) {
          return { ...b, data: { ...b.data, [fieldKey]: url } };
        }
        // Patch a specific item inside an object_list field. We need to know
        // which list to patch; walk the block's data to find any array whose
        // item at `itemIdx` has our fieldKey.
        const nextData = { ...b.data };
        for (const k of Object.keys(nextData)) {
          const arr = nextData[k];
          if (Array.isArray(arr) && arr[itemIdx] && Object.prototype.hasOwnProperty.call(arr[itemIdx], fieldKey)) {
            const nextArr = [...arr];
            nextArr[itemIdx] = { ...nextArr[itemIdx], [fieldKey]: url };
            nextData[k] = nextArr;
            break;
          }
        }
        return { ...b, data: nextData };
      })
    );
    closePicker();
  };

  const setPageField = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setPage((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/site-pages/${slug}`, page);
      setPage(data);
      toast.success("Saved. Changes are live.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-site-page-editor" className="bg-slate-50 min-h-full">
      <Toaster position="top-right" richColors />

      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 lg:px-8 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/admin/site-pages"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            All pages
          </Link>
          <div className="min-w-0">
            <p className="text-base font-bold text-slate-900 truncate">{page.title}</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">
              {page.public_path}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={page.public_path}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View live
          </a>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-site-page-save"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8 space-y-6 max-w-5xl">
        <PhantomEditWarning slug={slug} />
        {/* Draft / A-B controls */}
        <SitePageDraftPanel slug={slug} />

        {/* SEO / page meta */}
        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="text-sm font-bold text-slate-900 mb-4">Page metadata</h2>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Admin title</Label>
              <Input
                value={page.title || ""}
                onChange={setPageField("title")}
                className="mt-1"
                data-testid="admin-site-page-title"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">SEO title</Label>
              <Input
                value={page.seo_title || ""}
                onChange={setPageField("seo_title")}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">SEO description</Label>
              <Textarea
                value={page.seo_description || ""}
                onChange={setPageField("seo_description")}
                className="mt-1 min-h-[70px]"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">SEO keywords</Label>
              <Input
                value={page.seo_keywords || ""}
                onChange={setPageField("seo_keywords")}
                className="mt-1"
              />
            </div>
          </div>
        </section>

        {/* Blocks */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-900">
              Blocks <span className="text-slate-400 font-normal">({page.blocks.length})</span>
            </h2>
          </div>
          <div className="space-y-2.5">
            {page.blocks.map((b, i) => (
              <BlockCard
                key={b.id}
                block={b}
                index={i}
                totalCount={page.blocks.length}
                isOpen={!!openIds[b.id]}
                onToggle={() => setOpenIds((prev) => ({ ...prev, [b.id]: !prev[b.id] }))}
                onMove={(dir) => moveBlock(b.id, dir)}
                onRemove={() => removeBlock(b.id)}
                onChange={(next) => patchBlock(b.id, next)}
                onOpenPicker={openPicker}
              />
            ))}
          </div>

          {/* Add-block dropdown */}
          <div
            className="mt-4 border border-dashed border-slate-300 rounded-lg p-4 bg-white"
            data-testid="admin-site-page-add-block"
          >
            <div className="flex items-center flex-wrap gap-2">
              <span className="text-xs font-semibold text-slate-500">Add a block:</span>
              {BLOCK_TYPES.map((t) => (
                <Button
                  key={t}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addBlock(t)}
                  className="h-7 text-[11px]"
                  data-testid={`admin-site-page-add-${t}`}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {BLOCK_SCHEMAS[t].label}
                </Button>
              ))}
            </div>
          </div>
        </section>
      </div>

      <MediaPicker open={picker.open} onClose={closePicker} onPick={onPickImage} />
    </div>
  );
}
